import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Autocomplete, {
  type AutocompleteInputChangeReason,
  type AutocompleteRenderInputParams,
  type AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import type { BankFeedAutocompleteOption } from '@/domain/bankFeed/schemas';
import { useBankFeedValueDates } from '@/domain/bankFeed/queries';
import { useBankFeedRouteContext } from '@/pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from '@/pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import SelectedChips from '@/pages/company/BankFeedRoute/components/FilterToolbar/shared/SelectedChips';
import { sameIds } from '@/pages/company/BankFeedRoute/components/FilterToolbar/shared/sameIds';

const PAYMENT_DATE_WIDTH = 200;
const ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED_ICON = <CheckBoxIcon fontSize="small" />;

// Credit Card tab payment-date filter (DA-19485, multi-select since DPM-666). Searchable MUI
// Autocomplete whose options are the selected card + currency's discrete payment days (value_date),
// fetched via useBankFeedValueDates. Option ids are ISO 'YYYY-MM-DD' strings from the backend,
// stored verbatim in `toolbarFilters.paymentDates` and sent back unchanged as the request's `dates`
// set — no parsing/formatting round-trip, and never as date_from/date_to. Replaces the shared preset
// DateRangeFilter on the CC tab only.
const CardDateRangeFilter: React.FC = () => {
  const intl = useIntl();
  const { toolbarFilters, setToolbarFilters, accountType, summary, isSummaryLoading } =
    useBankFeedRouteContext();
  const { paymentDates, currency, accountIds } = toolbarFilters;

  // CARD carries exactly one card id (DA-19483); the value-dates endpoint takes a single account_id.
  const accountId = accountIds[0] ?? null;

  const { data, isInitialLoading: isOptionsLoading } = useBankFeedValueDates(accountType, accountId, currency);
  // options[0] is the newest payment day — the seeding below relies on that order.
  const options = useMemo(() => data?.options ?? [], [data]);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Working set while the menu is open; null when closed, so the closed field always renders the
  // applied selection. Apply-on-close: ticking a day must not write toolbarFilters or refetch.
  const [draft, setDraft] = useState<BankFeedAutocompleteOption[] | null>(null);

  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;

  // Derived from `options` rather than held as separate state: a refreshed option list then prunes
  // vanished days for free, and the resulting order is always the backend's newest-first one.
  const appliedOptions = useMemo(
    () => options.filter((o) => paymentDates.includes(o.id)),
    [options, paymentDates],
  );
  const selected = draft ?? appliedOptions;

  // Seed the newest payment day once the current (card, currency)'s options load, and re-seed when
  // that pair changes — the ticket's "select newest automatically on account/currency change". While
  // the new pair's options are still loading, clear the stale days so the grid gate (BankFeedRoute)
  // closes — otherwise the grid would fire one getRows with the new card/currency but the previous
  // pair's days. seededKeyRef marks a pair whose newest we've applied, so a manual selection within
  // the same pair is preserved; days that fell out of a refreshed list are dropped, and when none
  // survive the newest available day is applied instead.
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (accountId == null || currency == null) return;
    if (options.length === 0) {
      seededKeyRef.current = null;
      setToolbarFilters((prev) =>
        prev.paymentDates.length === 0 ? prev : { ...prev, paymentDates: [] },
      );
      return;
    }
    const key = `${accountId}|${currency}`;
    const survivors = appliedOptions.map((o) => o.id);
    const next = seededKeyRef.current === key && survivors.length > 0 ? survivors : [options[0].id];
    seededKeyRef.current = key;
    setToolbarFilters((prev) =>
      sameIds(prev.paymentDates, next) ? prev : { ...prev, paymentDates: next },
    );
  }, [accountId, currency, options, appliedOptions, setToolbarFilters]);

  const label = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.paymentDate.label',
    defaultMessage: 'Payment date',
  });

  const handleOpen = useCallback(() => {
    setDraft(appliedOptions);
    setOpen(true);
  }, [appliedOptions]);

  // Commit once on close. Unlike the sibling account filter there is no search-scoping: days ticked
  // before a query survive the commit even while the query hides them. Closing with nothing ticked
  // leaves the applied selection untouched (no-wipe guard) — an empty day set is not a representable
  // request on this tab, so there is no "no payment date" state and no clear affordance. The
  // committed order comes from `options`, not from the draft array: MUI appends each newly ticked
  // option at the end, which would make the chip show whichever day the user happened to click
  // first rather than the newest one.
  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue('');
    const draftIds = new Set((draft ?? []).map((o) => o.id));
    const next = options.filter((o) => draftIds.has(o.id)).map((o) => o.id);
    if (next.length > 0) {
      setToolbarFilters((prev) =>
        sameIds(prev.paymentDates, next) ? prev : { ...prev, paymentDates: next },
      );
    }
    setDraft(null);
  }, [draft, options, setToolbarFilters]);

  // Keep the typed search across a selection: MUI fires onInputChange with reason 'reset' when an
  // option is toggled, which would otherwise clear the field on every tick. Only react to actual
  // typing ('input') and the clear button ('clear') — same as the sibling account filter.
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') setInputValue(value);
      else if (reason === 'clear') setInputValue('');
    },
    [],
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
      option: BankFeedAutocompleteOption,
      { selected: isSelected }: AutocompleteRenderOptionState,
    ) => {
      const { key, ...liProps } = props;
      return (
        <li key={key} {...liProps}>
          <Checkbox icon={ICON} checkedIcon={CHECKED_ICON} checked={isSelected} sx={{ mr: 1, p: 0 }} />
          {option.label}
        </li>
      );
    },
    [],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={label}
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {isOptionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    ),
    [label, isOptionsLoading],
  );

  // Render the applied days as a chip + "+N" counter (matching the Bank account filter) when closed;
  // null while open so the search input shows for typing.
  const renderValue = useCallback(
    (value: BankFeedAutocompleteOption[]) => (open ? null : <SelectedChips picked={value} />),
    [open],
  );

  const isLoading = isSummaryLoading || isOptionsLoading;

  // At least one day is always applied once options resolve; until then (summary/options still
  // loading, a pre-data empty state, or no options at all) render a disabled placeholder field —
  // mirrors CardAccountFilter.
  if (isEmptyView || options.length === 0 || appliedOptions.length === 0) {
    return (
      <TextField
        label={label}
        size="small"
        disabled
        sx={{ width: PAYMENT_DATE_WIDTH, backgroundColor: 'common.white' }}
        slotProps={{
          input: {
            endAdornment: isLoading ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'action.disabled', mr: -0.75 }} />
            ),
          },
        }}
      />
    );
  }

  return (
    <Autocomplete<BankFeedAutocompleteOption, true, true, false>
      multiple
      disableCloseOnSelect
      disableClearable
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      size="small"
      sx={{
        width: PAYMENT_DATE_WIDTH,
        backgroundColor: 'common.white',
        '& .MuiInputBase-root': { flexWrap: 'nowrap' },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { pl: 1.75 },
        '& .MuiAutocomplete-input:first-of-type': open ? {} : { width: 0, minWidth: 0, p: 0 },
      }}
      options={options}
      value={selected}
      onChange={(_event, value) => setDraft(value)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      renderOption={renderOption}
      renderValue={renderValue}
      renderInput={renderInput}
    />
  );
};

export default CardDateRangeFilter;
